import { html, LitElement, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { SlhFormIcons } from "@slhui/form-icons";
import { SlhCoreDropdown, SlhCoreDropdownIcon } from "@slhuicore/dropdown";
import { style } from "./assets/dropdown.generated-style";

/**
 * Allow users control to select an option, or options, from a set of options, similar to a native <select> element
 *
 * @CustomElement slh-dropdown
 *
 * @cssprop --slh-default-font-family - Dropdown font family.
 * @cssprop --slh-base-xs-font-size - Dropdown font size.
 * @cssprop --slh-base-semi-bold-font-weight - Dropdown font weight.
 * @cssprop --slh-base-xxs-line-height - Dropdown font line height. Dropdown placeholder min height.
 * @cssprop --slh-color-neutral-shade-00 - Dropdown background color.
 * @cssprop --slh-color-neutral-shade-10 - Disabled and readonly dropdown background color. Scrollbar track color.
 * @cssprop --slh-color-neutral-shade-20 - Dropdown border color. Scrollbar thumb color.
 * @cssprop --slh-color-neutral-shade-40 - Disabled and readonly dropdown font color. Dropdown hover border color. Scrollbar hovered thumb color.
 * @cssprop --slh-color-neutral-shade-60 - Dropdown font color. Dropdown arrow color. Dropdown hint color.
 * @cssprop --slh-color-neutral-shade-80 - Open dropdown font color and hovered dropdown font color.
 * @cssprop --slh-color-neutral-shade-00 - Open dropdown background color.
 * @cssprop --slh-color-primary-shade-60 - Open dropdown border color.
 * @cssprop --slh-color-error-shade-20 - Error state dropdown background color.
 * @cssprop --slh-color-error-shade-80 - Error dropdown border color.
 * @cssprop --slh-color-error - Error hover and focus dropdown border color.
 * @cssprop --slh-color-neutral-shade-80-opacity-12 - Opened dropdown items holder shadow.
 * @cssprop --slh-base-bold-font-weight - Dropdown hint font weight.
 * @cssprop --slh-base-xxs-font-size - Dropdown hint font size.
 *
 * ** slots
 * @slot default - You should provide slh-dropdown-item component
 * @slot label - You should provide slh-label component. Assigned slot nodes are parsed so that only slh-label components are shown.
 * @slot hint - Custom content below the dropdown, designed to contain recommendations for user input.
 */

@customElement("slh-dropdown")
export default class SlhDropdown extends SlhCoreDropdown(LitElement) {
  static styles = style;

  /**
   * Render icon template
   * @returns {TemplateResult}
   * @param {SlhCoreDropdownIcon} icon
   */
  // eslint-disable-next-line class-methods-use-this
  protected renderIcon(icon: SlhCoreDropdownIcon): TemplateResult {
    return html`<slh-icon
      type="${icon.type}"
      color="${icon.color}"
    ></slh-icon>`;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDefaultIcon(): SlhCoreDropdownIcon {
    const type: string = this.getOpenState()
      ? IconType.ICO_DROPDOWN_UP
      : IconType.ICO_DROPDOWN_DOWN;
    return {
      type,
      color: "",
    };
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }
}
